import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import AuthenticationDependentMiddleware from 'components/middleware/AuthenticationDependentMiddleware';
import { isWithinSevenDays } from 'containers/login/utils/utils';
import { useActiveMember, useReduxSelector } from 'hooks';
import useIframeFeatures from 'hooks/iframe/useIframeFeatures/useIframeFeatures';
import useDailyReportPreviewSettingsUpdate from 'hooks/models/member-settings/useDailyReportPreviewSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useMemberWebSettingsUpdate from 'hooks/models/member/useMemberWebSettingsUpdate';
import { isEmpty, isNil } from 'lodash';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LocalStore from 'utils/localStorageUtils';
import './App.scss';
import { BeforeRoutes } from './beforeRoutes/BeforeRoutes';
import ConnectedCommandPallet from './ConnectedCommandPallet/ConnectedCommandPallet';
import ConnectedLoader from './ConnectedLoader/ConnectedLoader';
import ConnectedToast from './ConnectedToast/ConnectedToast';
import { AppRoutes } from './Routes';

const App = () => {
  const launchDarklyHasSynced = useReduxSelector((state) => state.launchDarkly.hasSynced);
  const isAuthenticated = useReduxSelector((state) => state.session.isAuthenticated);
  const shouldShowContent = !isAuthenticated || !!launchDarklyHasSynced;
  const memberSettings = useMemberSettings();
  const updateWebSettings = useMemberWebSettingsUpdate();
  const activeMember = useActiveMember();
  const navigate = useNavigate();
  const pathName = useLocation().pathname;
  const updateDailyReportSettings = useDailyReportPreviewSettingsUpdate();

  useIframeFeatures();

  useEffect(() => {
    if (isAuthenticated) {
      if (
        activeMember.firstName === 'John' &&
        activeMember.lastName === 'Doe' &&
        isNil(activeMember.phone) &&
        isWithinSevenDays(activeMember.organization!.createdOn) &&
        activeMember.id === activeMember.organization!.ownedBy &&
        pathName !== `/owner-onboarding`
      ) {
        navigate({ ...location, pathname: `/owner-onboarding` });
      }
      if (isNil(memberSettings?.web?.ui?.uiLocalStoreMigrated)) {
        const currentMemberUiSettings = memberSettings?.web?.ui;
        const localPreferences = LocalStore.get('preferences');
        const localNameFormat = localPreferences ? localPreferences.employeeNameFormat : 'FIRST_LAST';
        updateWebSettings('ui', {
          ...currentMemberUiSettings,
          uiLocalStoreMigrated: true,
          employeeNameFormat: localNameFormat,
        });
        LocalStore.remove('preferences');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, memberSettings?.web?.ui]);

  //remove any photos from field report custom sections on member settings
  useEffect(() => {
    const customSections =
      memberSettings?.web?.features?.dailyProjectReport?.dailyReportPreviewSettings?.customSections;

    if (!customSections) return;

    const needsReset = customSections.some((s) => !isEmpty(s.photos) || !isEmpty(s.additionalImages));

    if (!needsReset) return;

    const resetCustomSections = customSections.map((s) => ({
      ...s,
      photos: undefined,
      additionalImages: undefined,
    }));

    updateDailyReportSettings([{ key: 'customSections', payload: resetCustomSections }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSettings?.web?.features?.dailyProjectReport?.dailyReportPreviewSettings?.customSections]);

  return (
    <div className="app">
      <BeforeRoutes>
        <ConditionalContainer condition={!!shouldShowContent}>
          <AuthenticationDependentMiddleware>
            <AppRoutes />
          </AuthenticationDependentMiddleware>
        </ConditionalContainer>
      </BeforeRoutes>
      <ConnectedToast />
      <ConnectedLoader />
      <ConnectedCommandPallet />
    </div>
  );
};

export default App;
